import { useMessage } from "@common/hooks/message";
import { useDataImportController } from "../../../controllers/data-import";
import {
  SaveFormatPayload,
  SpreadsheetType,
  FormatInfo,
  SmartColumn,
  FormatDetails,
  FormatInfoWrapper,
  CellData,
  DeleteSsRowResponse,
} from "@modules/spreadsheets/dto/spreadsheet/type";
import { useFormatManagementController } from "../../../controllers/format-management";
import { I18n } from "react-redux-i18n";
import { useDataExportController } from "../../../controllers/data-export";
import { useBatchInvitesController } from "../../../controllers/batch-invites";
import React from "react";
import { addSSRow, deleteSSRow, loadIndividualSpreadsheetFormat, loadPedigreeSpreadsheetFormat, fetchSpreadsheetData, fetchSpreadsheetCount } from "@common/actions/table";
import {
  SpreadsheetTableRunPayload,
  SpreadsheetTableDefaultDataItem,
  SpreadsheetTableApiResponse,
  SpreadsheetTableWhereClause,
  SPREADSHEET_TABLE_CONSTANTS,
} from "../components/table/table.types";
import { useController as useTableController } from "../components/table/table.controller";
import { cleanSmartColumnsForApi, cleanQueryModelForApi, hydrateQueryModelFromSmartColumns, buildLoadIssQueryModelPayload } from "../components/table/data-converter";
import { computeQueryModel } from "../components/table/query.helpers";
import {
  SPREADSHEET_TYPE,
  OPERATOR_MAPPING,
  resolveFieldContextForApi,
  SPREADSHEET_FIELD_CONTEXT,
} from "@modules/spreadsheets/const/common";
import { useUndoRedo } from "../components/table/utils/undo-redo.hook";
import { buildRowKey } from "../components/table/utils/column.utils";
import { DeletedRowData, UndoRedoConfig } from "../components/table/utils/undo-redo.types";
import { CellUpdatePayload } from "@modules/spreadsheets/dto/spreadsheet/type";
import { updateCellValue } from "@common/actions/table";
import { SpreadsheetTableColumnId } from "../components/table/table.types";
import { saveFormat } from "@modules/spreadsheets/actions/spreadsheet";
import { HttpErrorDto } from "@common/dto/http";
import { createBatchLetters } from "@common/actions/letters";

export type SpreadsheetLiveTableRefs = {
    queryDetailsRef: React.RefObject<any>;
    activeFilterRef: React.RefObject<any>;
    formatDetailsRef: React.RefObject<any>;
} | null;

export type SpreadsheetResultProps = {
    spreadsheetType: SpreadsheetType;
    spreadsheetId: number;
    runPayload?: SpreadsheetTableRunPayload;
    isTemporaryRun?: boolean;
    onModeSwitch?: (mode: 'results' | 'management') => void;
    initialFolderId?: number;
    initialFamilyId?: number;
    liveTableRefs?: SpreadsheetLiveTableRefs;
};

const findGlobalAndRowRrn = (
  selectedRowData: SpreadsheetTableDefaultDataItem,
  dataSet: SpreadsheetTableApiResponse
) => {
  const rowId = Number(selectedRowData?.id);
  const selectedRowRrn = selectedRowData?.rrn;
  const rowData = dataSet?.rows[rowId];
  const globalId = rowData?.globalid;
  const pedigreeRowId = rowData?.id;


  return {
    globalId,
    selectedRowRrn,
    pedigreeRowId
  };
};

const getFormatDataFromRedux = (
  spreadsheetId: number | string | undefined,
  spreadsheetType: SpreadsheetType
) => {
  if (spreadsheetId == null || spreadsheetId === undefined || typeof window === "undefined" || !(window as any).__SPREADSHEET_STORE__) {
    return null;
  }
  try {
    const store = (window as any).__SPREADSHEET_STORE__;
    const state = store.getState();
    const spreadsheetTypeKey = spreadsheetType === SpreadsheetType.individual ? "Individual" : "Pedigree";
    const typeData = state?.spreadsheetFormat?.[spreadsheetTypeKey] || {};
    const storedData = typeData[Number(spreadsheetId)];
    if (storedData && storedData.columnModel && storedData.columnModel.smartColumns) {
      return {
        format: storedData.format,
        columnModel: storedData.columnModel,
        queryModel: storedData.queryModel ? JSON.parse(JSON.stringify(storedData.queryModel)) : { whereClauses: [] },
        filter: storedData.filter ? JSON.parse(JSON.stringify(storedData.filter)) : undefined,
      };
    }
  } catch (err) {
    console.warn("Could not retrieve spreadsheet format from Redux", err);
  }
  return null;
};

export const useController = (props: SpreadsheetResultProps) => {
  const { spreadsheetType } = props;
  const {
    showErrorMessage,
    messageProps,
    closeMessage,
    showInfoMessage,
    showConfirmMessage,
    showMessage,
  } = useMessage();

  const dataImportController = useDataImportController(() => {
    // Reload data when import dialog closes
    if (tableController.loadData) {
      tableController.loadData(true, 0);
    }
  });
  const formatManagementController = useFormatManagementController({
    spreadsheetType,
    showErrorMessage,
    queryBuilderConditions: [],
    currentFormat: undefined,
  });
  const dataExportController = useDataExportController();
  const [clearSelectionEvent, setClearSelectionEvent] = React.useState(false);

  const tableController = useTableController({
    spreadsheetId: Number(props.spreadsheetId),
    spreadsheetType: props.spreadsheetType,
    initialColumns: [],
    onColumnsChange: () => { },
  } as any);

  // Initialize undo/redo system at parent level
  const undoRedoConfig: UndoRedoConfig = React.useMemo(
    () => ({
      maxStackSize: 50,
      debounceMs: 500,
    }),
    []
  );

  const undoRedoCallbacks = React.useMemo(
    () => ({
      onCellEdit: async (
        payload: CellUpdatePayload,
        rowIndex: number,
        columnId: SpreadsheetTableColumnId,
        value: string | number,
        popPreviousUndo: boolean = false
      ) => {
        try {
          // Fix the context to use the correct spreadsheet type
          const correctedPayload = {
            ...payload,
            context:
              props.spreadsheetType === SpreadsheetType.pedigree
                ? SPREADSHEET_TYPE.pedigree
                : SPREADSHEET_TYPE.individual,
          };
          await updateCellValue(correctedPayload);

          const column = smartColumns[columnId];
          const typeId = column.field?.fieldtype?.typeid || column.field?.type
          const newValue = typeId === SPREADSHEET_TABLE_CONSTANTS.FIELD_TYPES.IMAGE ? { name: value } : value;

          // Update the specific cell in the UI state for immediate feedback
          if (currentData && currentData[rowIndex]) {
            const newData = [...currentData];
            if (columnId !== -1) {
              const key = buildRowKey(
                column.field.fieldname,
                column.field.fieldid,
                column.parentField?.fieldid
              );
              newData[rowIndex] = {
                ...newData[rowIndex],
                [key]: newValue,
              };
              setCurrentData(newData);
            }
          }
          // Also update dataResult to keep it synchronized
          if (dataResult && dataResult.data && dataResult.data[rowIndex]) {
            const newDataResult = { ...dataResult };
            const rowData = newDataResult.data[rowIndex];
            const cellData = rowData[columnId];
            if (cellData) {
              cellData.rawValue = newValue;
              cellData.dataValue = newValue;
              cellData.updateValue = newValue;
              setDataResult(newDataResult);
            }
          }
        } catch (error) {
          console.error("Error in cell edit callback:", error);
          showErrorMessage("Failed to update cell value");
        }
      },
      onBulkCellUpdate: async (
        updates: Array<{
          rowIndex: number;
          columnId: SpreadsheetTableColumnId;
          value: string | number;
          payload: CellUpdatePayload;
          columnIndex: number
        }>
      ) => {
        try {
          // Make all API calls in parallel
          const apiPromises = updates.map(async (update) => {
            const correctedPayload = {
              ...update.payload,
              context:
                props.spreadsheetType === SpreadsheetType.pedigree
                  ? SPREADSHEET_TYPE.pedigree
                  : SPREADSHEET_TYPE.individual,
            };
            return updateCellValue(correctedPayload);
          });

          await Promise.all(apiPromises);

          // Update UI state in bulk after all API calls succeed
          if (currentData && dataResult) {
            const newData = [...currentData];
            const newDataResult = { ...dataResult };

            for (const update of updates) {
              const { rowIndex, columnId, value, columnIndex } = update;
              const column = smartColumns[columnIndex];
              const typeId = column.field?.fieldtype?.typeid || column.field?.type
              const newValue = typeId === SPREADSHEET_TABLE_CONSTANTS.FIELD_TYPES.IMAGE ? { name: value } : value;
              // Update currentData
              if (newData[rowIndex]) {
                const key = buildRowKey(
                  column.field.fieldname,
                  column.field.fieldid,
                  column.parentField?.fieldid
                );

                newData[rowIndex] = {
                  ...newData[rowIndex],
                  [key]: newValue,
                };
              }

              // Update dataResult
              if (newDataResult.data && newDataResult.data[rowIndex]) {
                const rowData = newDataResult.data[rowIndex];
                const cellData = rowData[columnId];

                if (cellData) {
                  cellData.rawValue = newValue;
                  cellData.dataValue = newValue;
                  cellData.updateValue = newValue;
                }
              }
            }

            // Update both states at once
            setCurrentData(newData);
            setDataResult(newDataResult);
          }
        } catch (error) {
          console.error("Error in bulk cell update callback:", error);
          showErrorMessage("Failed to update multiple cell values");
          throw error; // Re-throw to let the undo-redo manager handle it
        }
      },
      onRowAdd: async (
        rowIndex: number,
        columnId: SpreadsheetTableColumnId,
        globalId: number,
        tableId: number,
        rrn: number
      ) => {
        try {
          // Use cleaned columns to reduce payload size (dropdown/lookup values removed)
          const response = await addSSRow({
            pss: props.spreadsheetType === SpreadsheetType.pedigree,
            rrn: -1,
            column: columnId,
            globalId,
            columnModel: {
              smartColumns: cleanSmartColumnsForApi(smartColumns || []),
            },
          });
          // Update UI directly like normal add row operation
          if (currentData && response && response.data && response.rrn) {
            const newRow: SpreadsheetTableDefaultDataItem = {
              id: String(rowIndex), // Use the provided rowIndex
              rowCount: rowIndex + 1, // Adjust rowCount
              rrn: response.rrn,
            };

            const responseDataForRow =
              response.data?.data || response.data || [];
            if (responseDataForRow && responseDataForRow.length > 0) {
              responseDataForRow.forEach((cellData: CellData) => {
                if (cellData.field && cellData.field.fieldname) {
                  const parentFieldId = smartColumns[columnId].parentField?.fieldid;
                  if (columnId !== -1 && parentFieldId !== undefined) {
                    const key = buildRowKey(cellData.field.fieldname, cellData.field.fieldid, parentFieldId);
                    newRow[key] = cellData.dataValue ?? cellData.rawValue ?? "";
                  }
                }
              });
            }

            const newData = [...currentData];
            newData.splice(rowIndex, 0, newRow);

            // Update IDs and rowCount for all rows after the insertion
            for (let i = rowIndex + 1; i < newData.length; i++) {
              newData[i].id = String(i);
              newData[i].rowCount = i + 1;
            }
            setCurrentData(newData);
            setTotalCount((prev) => (prev ? prev + 1 : prev));

            // Also update dataResult to keep it synchronized
            if (
              dataResult &&
              dataResult.data &&
              Array.isArray(dataResult.data)
            ) {
              const newDataResult = { ...dataResult };

              // Get the existing row structure to understand the pattern
              const existingRow = newDataResult.data[0];
              if (existingRow && Array.isArray(existingRow)) {
                const rowLength = existingRow.length;

                // Create a complete new row array with the same structure
                const newRowData: (CellData | Record<string, never>)[] =
                  new Array(rowLength).fill({});

                // Set the first dummy object (fieldid: -599)
                newRowData[0] = {
                  modified: false,
                  valid: true,
                  rawValue: 0,
                  dataValue: 0,
                  updateValue: 0,
                  constraintViolation: null,
                  field: {
                    fieldid: SPREADSHEET_TABLE_CONSTANTS.ROW_COUNT_FIELD_ID,
                    fieldname: "unknown field",
                    permissions: "WRITE",
                    tableIndex: 0,
                    defaultWidth: 100,
                    fieldClass: "java.lang.Integer",
                    system: true,
                    systemDataField: false,
                    required: false,
                    fieldnameWithPath: null,
                    fieldcontext: spreadsheetType === SpreadsheetType.individual
                      ? SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL
                      : SPREADSHEET_FIELD_CONTEXT.PEDIGREE,
                    dataSourceNodeId: 0,
                    fieldtype: {
                      typeid: 3,
                      typename: "Number",
                      imageURL: "nbrhi.bmp",
                      computed: false,
                      systemType: false,
                    },
                    dropdownValues: [],
                  },
                };

                // Set the last dummy object (fieldid: -500)
                newRowData[rowLength - 1] = {
                  modified: false,
                  valid: true,
                  rawValue: "",
                  dataValue: "",
                  updateValue: "",
                  constraintViolation: null,
                  field: {
                    fieldid: -500,
                    fieldname: "",
                    permissions: "WRITE",
                    tableIndex: 0,
                    defaultWidth: 100,
                    fieldClass: "java.lang.String",
                    system: true,
                    systemDataField: false,
                    required: false,
                    fieldnameWithPath: null,
                    fieldcontext: spreadsheetType === SpreadsheetType.individual
                      ? SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL
                      : SPREADSHEET_FIELD_CONTEXT.PEDIGREE,
                    dataSourceNodeId: 0,
                    fieldtype: {
                      typeid: 3,
                      typename: "Number",
                      imageURL: "nbrhi.bmp",
                      computed: false,
                      systemType: false,
                    },
                    dropdownValues: [],
                  },
                };

                // Fill the middle positions with response data
                const responseDataForDataResult =
                  response.data?.data || response.data || [];

                let responseDataIndex = 0;
                for (let i = 1; i < rowLength - 1; i++) {
                  const currentSmartColumn = smartColumns[i];
                  const { parentField } = currentSmartColumn;
                  if (parentField) {
                    // This is the column where add row was clicked, insert the response data
                    if (
                      responseDataForDataResult &&
                      responseDataForDataResult.length > 0
                    ) {
                      const responseCell =
                        responseDataForDataResult[responseDataIndex];

                      if (
                        currentSmartColumn?.field?.fieldname ===
                        responseCell?.field?.fieldname
                      ) {
                        newRowData[i] = {
                          ...responseCell,
                          rrn: response.rrn,
                        };
                        responseDataIndex++;
                      }
                    }
                  }
                }

                // Insert the new row data into dataResult.data
                newDataResult.data.splice(rowIndex, 0, newRowData);

                // If dataResult has rows property, update it too
                if (newDataResult.rows && Array.isArray(newDataResult.rows)) {
                  const newRowMeta = {
                    globalid: globalId,
                    id: globalId, // Use the same ID for both fields since this is the correct ID passed from undo/redo
                    rrn: response.rrn,
                  };
                  newDataResult.rows.splice(rowIndex, 0, newRowMeta);
                }

                setDataResult(newDataResult);
              }
            }
          }

          return { rrn: response.rrn };
        } catch (error) {
          console.error("Error in row add callback:", error);
          showErrorMessage("Failed to add row");
          throw error;
        }
      },
      onRowDelete: async (
        rowIndex: number,
        columnId: SpreadsheetTableColumnId,
        globalId: number,
        tableId: number,
        rrn: number
      ) => {
        try {
          // Use cleaned columns to reduce payload size (dropdown/lookup values removed)
          await deleteSSRow({
            pss: props.spreadsheetType === SpreadsheetType.pedigree,
            rrn: rrn,
            column: columnId,
            columnModel: {
              smartColumns: cleanSmartColumnsForApi(smartColumns || []),
            },
          });

          // Update UI directly like normal delete operation
          if (currentData && currentData[rowIndex]) {
            const newData = [...currentData];
            newData.splice(rowIndex, 1);

            // Update IDs and rowCount for remaining rows
            for (let i = 0; i < newData.length; i++) {
              newData[i].id = String(i);
              newData[i].rowCount = i + 1;
            }

            setCurrentData(newData);
            setTotalCount((prev) => (prev ? prev - 1 : prev));
          }

          // Also update dataResult to keep it synchronized
          if (dataResult && dataResult.data && dataResult.data[rowIndex]) {
            const newDataResult = { ...dataResult };
            newDataResult.data.splice(rowIndex, 1);

            // Also remove from rows if it exists
            if (newDataResult.rows && Array.isArray(newDataResult.rows)) {
              newDataResult.rows.splice(rowIndex, 1);
            }

            setDataResult(newDataResult);
          }
        } catch (error) {
          console.error("Error in row delete callback:", error);
          showErrorMessage("Failed to delete row");
        }
      },
      getFieldInfo: (fieldId: number) => {
        const field = smartColumns?.find(
          (col) => col?.field?.fieldid === fieldId
        );
        return field?.field ? { system: field.field.system } : undefined;
      },

      getCurrentRowData: () => {
        // Return current row data with RRN and row index for redo operations
        return currentData?.map((row, index) => ({
          rrn: row.rrn || -1,
          rowIndex: index,
          ...row
        }));
      },

      // NEW: Redo operation callbacks that don't update state immediately
      onRowDeleteWithoutStateUpdate: async (
        rowIndex: number,
        columnId: SpreadsheetTableColumnId,
        globalId: number,
        tableId: number,
        rrn: number
      ) => {
        try {
          // Use cleaned columns to reduce payload size (dropdown/lookup values removed)
          await deleteSSRow({
            pss: props.spreadsheetType === SpreadsheetType.pedigree,
            rrn: rrn,
            column: columnId,
            columnModel: {
              smartColumns: cleanSmartColumnsForApi(smartColumns || []),
            },
          });

          // DON'T update state - just return
        } catch (error) {
          console.error("Error in row delete without state update:", error);
          showErrorMessage("Failed to delete row");
          throw error;
        }
      },

      onBulkStateUpdateForRedo: async (bulkUpdateData: {
        rowDeletions: Array<{
          rowIndex: number;
          columnId: SpreadsheetTableColumnId;
          globalId: number;
          tableId: number;
          rrn: number;
        }>;
        totalCountChange: number;
      }) => {
        try {
          const { rowDeletions, totalCountChange } = bulkUpdateData;

          // Create working copy
          let workingCurrentData = [...currentData];
          let workingDataResult = dataResult ? { ...dataResult } : null;
          let workingTotalCount = totalCount;

          // Sort deletions by row index in descending order to delete from bottom to top
          const sortedDeletions = [...rowDeletions].sort((a, b) => b.rowIndex - a.rowIndex);

          // Remove rows from working data
          for (const deletion of sortedDeletions) {
            const { rowIndex } = deletion;

            // Remove from currentData
            workingCurrentData.splice(rowIndex, 1);

            // Remove from dataResult
            if (workingDataResult && workingDataResult.data && Array.isArray(workingDataResult.data)) {
              workingDataResult.data.splice(rowIndex, 1);
            }
            if (workingDataResult && workingDataResult.rows && Array.isArray(workingDataResult.rows)) {
              workingDataResult.rows.splice(rowIndex, 1);
            }
          }

          // Update all row IDs and rowCounts in working data
          for (let i = 0; i < workingCurrentData.length; i++) {
            workingCurrentData[i].id = String(i);
            workingCurrentData[i].rowCount = i + 1;
          }

          // Update total count
          workingTotalCount = workingTotalCount ? workingTotalCount + totalCountChange : workingTotalCount;

          // Single state update with final working objects
          setCurrentData(workingCurrentData);
          setDataResult(workingDataResult);
          setTotalCount(workingTotalCount);

        } catch (error) {
          console.error("Error in bulk state update for redo:", error);
          showErrorMessage("Failed to update spreadsheet state during redo");
          throw error;
        }
      },

      onError: (error: string) => {
        showErrorMessage(error);
      },

      // NEW: Bulk operation callbacks that don't update state immediately
      onRowAddWithoutStateUpdate: async (
        rowIndex: number,
        columnId: SpreadsheetTableColumnId,
        globalId: number,
        tableId: number,
        rrn: number
      ) => {
        try {
          // Use cleaned columns to reduce payload size (dropdown/lookup values removed)
          const response = await addSSRow({
            pss: props.spreadsheetType === SpreadsheetType.pedigree,
            rrn: -1,
            column: columnId,
            globalId,
            columnModel: {
              smartColumns: cleanSmartColumnsForApi(smartColumns || []),
            },
          });

          // Return response data but DON'T update state
          return {
            rrn: response.rrn,
            response: response
          };
        } catch (error) {
          console.error("Error in row add without state update:", error);
          showErrorMessage("Failed to add row");
          throw error;
        }
      },

      onBulkCellUpdateWithoutStateUpdate: async (
        updates: Array<{
          rowIndex: number;
          columnId: SpreadsheetTableColumnId;
          value: string | number;
          payload: CellUpdatePayload;
        }>
      ) => {
        try {
          // Make all API calls in parallel
          const apiPromises = updates.map(async (update) => {
            const correctedPayload = {
              ...update.payload,
              context: props.spreadsheetType === SpreadsheetType.pedigree
                ? SPREADSHEET_TYPE.pedigree
                : SPREADSHEET_TYPE.individual,
            };
            return updateCellValue(correctedPayload);
          });

          await Promise.all(apiPromises);

          // DON'T update state - just return
        } catch (error) {
          console.error("Error in bulk cell update without state update:", error);
          showErrorMessage("Failed to update multiple cell values");
          throw error;
        }
      },

      onBulkStateUpdate: async (bulkUpdateData: {
        rowAdditions: Array<{
          rowIndex: number;
          columnId: SpreadsheetTableColumnId;
          globalId: number;
          tableId: number;
          rrn: number;
          response: any;
        }>;
        cellUpdates: Array<{
          rowIndex: number;
          columnId: SpreadsheetTableColumnId;
          value: string | number;
          payload: CellUpdatePayload;
          columnIndex: number
        }>;
        totalCountChange: number;
      }) => {
        try {
          const { rowAdditions, cellUpdates, totalCountChange } = bulkUpdateData;

          // Create working copies (dummy objects)
          let workingCurrentData = [...currentData];
          let workingDataResult = dataResult ? { ...dataResult } : null;
          let workingTotalCount = totalCount;

          // Step 1: Sort row additions by original position (ascending) to insert from top to bottom
          const sortedRowAdditions = [...rowAdditions].sort((a, b) => a.rowIndex - b.rowIndex);

          // Step 2: Insert rows at their original positions (from top to bottom to avoid index shifting)
          for (const rowAddition of sortedRowAdditions) {
            const { rowIndex, response, globalId, tableId } = rowAddition;

            // Create new row object
            const newRow: SpreadsheetTableDefaultDataItem = {
              id: String(rowIndex), // Will be corrected later
              rowCount: rowIndex + 1, // Will be corrected later
              rrn: response.rrn,
            };

            // Add cell data to row from API response
            const responseDataForRow = response.data?.data || response.data || [];
            if (responseDataForRow && responseDataForRow.length > 0) {
              responseDataForRow.forEach((cellData: CellData) => {
                if (cellData.field && cellData.field.fieldname) {
                  const smartColumnIndex = smartColumns.findIndex(
                    (col) => {
                      const fieldNameMatch = col.field?.fieldname === cellData.field.fieldname
                      const tableIdMatch = col.parentField?.fieldid === tableId
                      return fieldNameMatch && tableIdMatch
                    }
                  );

                  const parentFieldId = smartColumns[smartColumnIndex].parentField?.fieldid;
                  if (smartColumnIndex !== -1 && parentFieldId) {
                    const key = buildRowKey(cellData.field.fieldname, cellData.field.fieldid, parentFieldId);
                    newRow[key] = cellData.dataValue ?? cellData.rawValue ?? "";
                  }
                }
              });
            }

            // Insert row at its original position
            workingCurrentData.splice(rowIndex, 0, newRow);

            // Update working dataResult
            if (workingDataResult && workingDataResult.data && Array.isArray(workingDataResult.data)) {
              const existingRow = workingDataResult.data[0];
              if (existingRow && Array.isArray(existingRow)) {
                const rowLength = existingRow.length;
                const newRowData: (CellData | Record<string, never>)[] = new Array(rowLength).fill({});

                // Set dummy objects
                newRowData[0] = {
                  modified: false,
                  valid: true,
                  rawValue: 0,
                  dataValue: 0,
                  updateValue: 0,
                  constraintViolation: null,
                  field: {
                    fieldid: SPREADSHEET_TABLE_CONSTANTS.ROW_COUNT_FIELD_ID,
                    fieldname: "unknown field",
                    permissions: "WRITE",
                    tableIndex: 0,
                    defaultWidth: 100,
                    fieldClass: "java.lang.Integer",
                    system: true,
                    systemDataField: false,
                    required: false,
                    fieldnameWithPath: null,
                    fieldcontext: spreadsheetType === SpreadsheetType.individual
                      ? SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL
                      : SPREADSHEET_FIELD_CONTEXT.PEDIGREE,
                    dataSourceNodeId: 0,
                    fieldtype: {
                      typeid: 3,
                      typename: "Number",
                      imageURL: "nbrhi.bmp",
                      computed: false,
                      systemType: false,
                    },
                    dropdownValues: [],
                  },
                };

                newRowData[rowLength - 1] = {
                  modified: false,
                  valid: true,
                  rawValue: "",
                  dataValue: "",
                  updateValue: "",
                  constraintViolation: null,
                  field: {
                    fieldid: -500,
                    fieldname: "",
                    permissions: "WRITE",
                    tableIndex: 0,
                    defaultWidth: 100,
                    fieldClass: "java.lang.String",
                    system: true,
                    systemDataField: false,
                    required: false,
                    fieldnameWithPath: null,
                    fieldcontext: spreadsheetType === SpreadsheetType.individual
                      ? SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL
                      : SPREADSHEET_FIELD_CONTEXT.PEDIGREE,
                    dataSourceNodeId: 0,
                    fieldtype: {
                      typeid: 3,
                      typename: "Number",
                      imageURL: "nbrhi.bmp",
                      computed: false,
                      systemType: false,
                    },
                    dropdownValues: [],
                  },
                };

                // Fill middle positions with response data
                const responseDataForDataResult = response.data?.data || response.data || [];
                let responseDataIndex = 0;
                for (let i = 1; i < rowLength - 1; i++) {
                  const currentSmartColumn = smartColumns[i];
                  const { parentField } = currentSmartColumn;
                  if (parentField) {
                    if (responseDataForDataResult && responseDataForDataResult.length > 0) {
                      const responseCell = responseDataForDataResult[responseDataIndex];
                      if (currentSmartColumn?.field?.fieldname === responseCell?.field?.fieldname) {
                        newRowData[i] = {
                          ...responseCell,
                          rrn: response.rrn,
                        };
                        responseDataIndex++;
                      }
                    }
                  }
                }

                // Insert at original position in working dataResult
                workingDataResult.data.splice(rowIndex, 0, newRowData);

                if (workingDataResult.rows && Array.isArray(workingDataResult.rows)) {
                  const newRowMeta = {
                    globalid: globalId,
                    rrn: response.rrn,
                  };
                  workingDataResult.rows.splice(rowIndex, 0, newRowMeta);
                }
              }
            }
          }

          // Step 3: Update all row IDs and rowCounts in working data
          for (let i = 0; i < workingCurrentData.length; i++) {
            workingCurrentData[i].id = String(i);
            workingCurrentData[i].rowCount = i + 1;
          }

          // Step 4: Process cell updates using correct row indices
          for (const update of cellUpdates) {
            const { rowIndex, columnId, value, payload, columnIndex } = update;

            // Find the correct row index in working data by matching RRN
            const correctRowIndex = workingCurrentData.findIndex(row => row.rrn === payload.rrn);

            if (correctRowIndex !== -1) {
              // Update working currentData
              const column = smartColumns[columnIndex]
              const key = buildRowKey(
                column.field.fieldname,
                column.field.fieldid,
                column.parentField?.fieldid
              );
              workingCurrentData[correctRowIndex] = {
                ...workingCurrentData[correctRowIndex],
                [key]: value,
              };

              // Update working dataResult
              if (workingDataResult && workingDataResult.data && workingDataResult.data[correctRowIndex]) {
                const rowData = workingDataResult.data[correctRowIndex];
                const cellData = rowData[columnIndex]
                if (cellData) {
                  cellData.rawValue = value;
                  cellData.dataValue = value;
                  cellData.updateValue = value;
                }
              }
            }
          }

          // Step 5: Update total count
          workingTotalCount = workingTotalCount ? workingTotalCount + totalCountChange : workingTotalCount;

          // Step 6: Single state update with final working objects
          setCurrentData(workingCurrentData);
          setDataResult(workingDataResult);
          setTotalCount(workingTotalCount);

        } catch (error) {
          console.error("Error in bulk state update:", error);
          showErrorMessage("Failed to update spreadsheet state");
          throw error;
        }
      },
    }),
    [props.spreadsheetType, showErrorMessage, tableController.handleLoadData]
  );

  const {
    trackCellEdit,
    trackRowAdd,
    trackRowDelete,
    trackBulkFill,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useUndoRedo(undoRedoConfig, undoRedoCallbacks);

  const sendBatchInvitesController = useBatchInvitesController({
    spreadsheetId: Number(props.spreadsheetId),
    folderId: props.runPayload?.folderId ?? props.runPayload?.ssFormat?.folderId ?? props.initialFolderId ?? 0,
    familyId: props.runPayload?.familyId ?? props.initialFamilyId ?? 0,
    queryModel: props.runPayload?.queryModel,
    filterModel: { whereClauses: [] }, // Default empty filter
    showErrorMessage,
    showInfoMessage,
    showMessage,
    getFolderId: () => {
      // Try to get folder ID from various sources
      const runPayloadFolderId =
        props.runPayload?.folderId ??
        props.runPayload?.ssFormat?.folderId ??
        props.initialFolderId;
      const formatDetailsFolderId =
        tableController.formatDetailsRef?.current?.folderId;

      // Get data folder ID from loaded spreadsheet data (this is the correct one!)
      const dataFolderId = (tableController.dataResult?.rows?.[0] as any)
        ?.folderid;

      const finalFolderId =
        dataFolderId || runPayloadFolderId || formatDetailsFolderId || 0;

      return finalFolderId;
    },
    getCurrentQueryModel: () => {
      return (
        tableController.queryDetailsRef?.current ||
        props.runPayload?.queryModel
      );
    },
    getCurrentFilterModel: () => {
      return (
        tableController.activeFilterRef?.current ||
        (props.runPayload as any)?.filter ||
        { whereClauses: [] }
      );
    },
  });

  const [selectedRowData, setSelectedRowData] = React.useState<
    SpreadsheetTableDefaultDataItem[]
  >([]);
  const [currentData, setCurrentData] = React.useState<any[]>([]);
  const [smartColumns, setSmartColumns] = React.useState<any[]>([]);
  const [dataResult, setDataResult] = React.useState<any>(null);
  const [totalCount, setTotalCount] = React.useState<number | null>(null);
  const [hasRrn, setHasRrn] = React.useState(false);
  const [hasParentField, setHasParentField] = React.useState(false);
  const [selectedColumnIndex, setSelectedColumnIndex] = React.useState<
    number | null
  >(null);
  const [canAddRow, setCanAddRow] = React.useState(false);
  const [canDeleteRow, setCanDeleteRow] = React.useState(false);
  const [undoRedoState, setUndoRedoState] = React.useState({
    canUndo: false,
    canRedo: false,
    onUndo: () => { },
    onRedo: () => { },
  });
  const [isSaveFormatLoading, setIsSaveFormatLoading] = React.useState(false);
  const [spreadsheetId, setSpreadsheetId] = React.useState<number>(0);
  const [isAddingRow, setIsAddingRow] = React.useState(false);
  const [isDeletingRow, setIsDeletingRow] = React.useState(false);
  const [isProcessingUndoRedo, setIsProcessingUndoRedo] = React.useState(false);

  const getOperatorValue = React.useCallback((operator: string): number => {
    const operatorMap: Record<string, number> = OPERATOR_MAPPING;
    return operatorMap[operator] || 1; // Default to EQUALS if not found
  }, []);

  React.useEffect(() => {
    if (isAddingRow) {
      setIsAddingRow(false);
    }
    if (isDeletingRow) {
      setIsDeletingRow(false);
      setClearSelectionEvent(true)
    }
  }, [dataResult]);

  const undoWithLoader = React.useCallback(async () => {
    let loaderShown = false;
    const loaderTimer = setTimeout(() => {
      loaderShown = true;
      setIsProcessingUndoRedo(true);
    }, 500);

    try {
      await undo();
    } finally {
      clearTimeout(loaderTimer);
      if (loaderShown) {
        setIsProcessingUndoRedo(false);
      }
    }
  }, [undo]);

  const redoWithLoader = React.useCallback(async () => {
    let loaderShown = false;
    const loaderTimer = setTimeout(() => {
      loaderShown = true;
      setIsProcessingUndoRedo(true);
    }, 500);

    try {
      await redo();
    } finally {
      clearTimeout(loaderTimer);
      if (loaderShown) {
        setIsProcessingUndoRedo(false);
      }
    }
  }, [redo])


  // Update undo/redo state for parent components
  React.useEffect(() => {
    const effectiveCanUndo = Boolean(canUndo) && !isProcessingUndoRedo;
    const effectiveCanRedo = Boolean(canRedo) && !isProcessingUndoRedo;
    setUndoRedoState({
      canUndo: effectiveCanUndo,
      canRedo: effectiveCanRedo,
      onUndo: undoWithLoader,
      onRedo: redoWithLoader,
    });
  }, [canUndo, canRedo, undoWithLoader, redoWithLoader, isProcessingUndoRedo]);

  const handleLoadFormat = React.useCallback(
    async (format: FormatInfo) => {
      try {
        if (!format?.id) {
          showErrorMessage(I18n.t("Invalid format selected"));
          return;
        }

        // Load format data based on spreadsheet type
        const loadFormatApi = spreadsheetType === SpreadsheetType.individual
          ? loadIndividualSpreadsheetFormat
          : loadPedigreeSpreadsheetFormat;

        const formatData = await loadFormatApi(format.id);

        if (!formatData) {
          showErrorMessage(I18n.t("Failed to load format data"));
          return;
        }

        // Extract format details - handle different response structures
        const columnModel = formatData.columnModel || formatData;
        // Remove dummy/unknown field(s) here so downstream (builder/sessionStorage) doesn't receive them
        const filteredColumnModel = {
          ...columnModel,
          smartColumns: (columnModel.smartColumns || []).filter(
            (smartCol: SmartColumn) =>
              smartCol?.field?.fieldid && smartCol.field.fieldid > 0
          ),
        };
        const queryModel = formatData.queryModel;
        const formatDetails = formatData.formatDetails;

        if (!columnModel?.smartColumns) {
          showErrorMessage(
            I18n.t("Invalid format structure - no columns found")
          );
          return;
        }

        // Enrich queryModel with dropdown/options/lookup values from smartColumns before storing
        const enrichedQueryModel = hydrateQueryModelFromSmartColumns(queryModel, filteredColumnModel.smartColumns);

        if (typeof window !== "undefined" && (window as any).__SPREADSHEET_STORE__) {
          try {
            const store = (window as any).__SPREADSHEET_STORE__;
            const { storeFormatData, clearFormatData } = require("@modules/spreadsheets/actions/spreadsheetFormat");
            const spreadsheetTypeKey =
              spreadsheetType === SpreadsheetType.individual ? "Individual" : "Pedigree";
            const columnModelForStorage = {
              ...(columnModel || {}),
              smartColumns: filteredColumnModel.smartColumns.map((smartCol: SmartColumn) =>
                JSON.parse(JSON.stringify(smartCol))
              ),
            };
            // Use enriched queryModel for storage
            const queryModelForStorage = enrichedQueryModel
              ? JSON.parse(JSON.stringify(enrichedQueryModel))
              : { whereClauses: [] };
            const formatDataToStore: any = {
              format: {
                id: Number(format.id),
                name: format.name || "",
                description: format.description || "",
                folderId: format.folderId || 0,
              },
              columnModel: columnModelForStorage,
              queryModel: queryModelForStorage,
              timestamp: Date.now(),
            };
            if ((formatData as any)?.filter) {
              formatDataToStore.filter = JSON.parse(JSON.stringify((formatData as any).filter));
            }

            // If we're in a new spreadsheet flow (non-positive temporary ID), clear it from Redux
            // since we're now loading an existing format
            if (props.spreadsheetId <= 0) {
              store.dispatch(clearFormatData(Number(props.spreadsheetId), spreadsheetTypeKey));
            }

            // Store the loaded format data with the loaded format ID only
            store.dispatch(storeFormatData(Number(format.id), spreadsheetTypeKey, formatDataToStore));
          } catch (e) {
            console.warn("Could not store loaded format in Redux:", e);
          }
        }

        // Update all references with loaded format data

        // Update table controller with loaded format data
        if (tableController.setSmartColumns) {
          tableController.setSmartColumns(filteredColumnModel.smartColumns);
        }

        // Update columns state for UI display (exclude dummy/unknown field)
        if (tableController.setColumns) {
          // Simple column mapping for loaded format
          const mappedColumns = filteredColumnModel.smartColumns
            .filter(
              (smartCol: SmartColumn) =>
                smartCol?.field?.fieldid && smartCol.field.fieldid > 0
            )
            .map((smartCol: SmartColumn, idx: number) => ({
              id: smartCol.field?.fieldid || 0,
              header: smartCol.field?.fieldname || "",
              colName: smartCol.field?.fieldname || "",
              accessorKey: smartCol.field?.fieldname
                ? buildRowKey(smartCol.field?.fieldname, smartCol.field?.fieldid)
                : "",
              enableResizing: true,
              size: smartCol.width || 100,
              minSize: 50,
              maxSize: 500,
              type: "text",
              editable: smartCol.editable || false,
              sortOrder: smartCol.sortOrder || 0,
              sortAsc: smartCol.sortAsc || false,
            }));
          tableController.setColumns(mappedColumns);
        }

        // Update format details reference
        if (tableController.formatDetailsRef?.current !== undefined) {
          tableController.formatDetailsRef.current = formatDetails || {
            id: format.id,
            name: format.name,
            description: format.description,
            folderId: format.folderId,
          };
        }

        // Update query model if present
        if (
          queryModel &&
          tableController.queryDetailsRef?.current !== undefined
        ) {
          tableController.queryDetailsRef.current = queryModel;
        }

        // Update column model reference
        if (tableController.columnModelRef?.current !== undefined) {
          tableController.columnModelRef.current = filteredColumnModel;
        }

        // Store format data and redirect to builder

        // Close the format management dialog
        formatManagementController.closeLoadFormat();

        // Store the loaded format data in sessionStorage so the builder can access it
        const formatDataKey = `loadedFormat_${spreadsheetType}_${format.id}`;
        const formatDataToStore = {
          format: {
            id: format.id,
            name: format.name,
            description: format.description,
            folderId: format.folderId,
          },
          columnModel: filteredColumnModel,
          queryModel,
          timestamp: Date.now(),
        };

        try {
          sessionStorage.setItem(
            formatDataKey,
            JSON.stringify(formatDataToStore)
          );

          const { executeUI } = await import("@common/utils/ui-bridge");

          // Determine the action based on spreadsheet type
          const action =
            spreadsheetType === SpreadsheetType.individual
              ? "openISS"
              : "openPSS";

          // Execute UI action to open builder
          executeUI("SPREADSHEETS", action, format.id, format.name, null);
        } catch (bridgeError) {
          // Silent fail - user can manually navigate to builder
        }
      } catch (error) {
        const errorMessage =
          (error as HttpErrorDto)?.entity ||
          (error as Error)?.message ||
          I18n.t("Failed to load format");
        showErrorMessage(errorMessage);
      }
    },
    [
      spreadsheetType,
      tableController,
      formatManagementController,
      showErrorMessage,
      showInfoMessage,
    ]
  );
  const handleEdit = async () => {
    try {
      // trigger mode switch
      // This will be handled by the parent component
      if (props.onModeSwitch) {
        props.onModeSwitch('management');
      }
    } catch (err: any) {
      showErrorMessage(err?.entity || err?.message || I18n.t('Failed to switch to edit mode'));
    }
  };


    const saveFormatInternal = React.useCallback(
        async (formatDetails: FormatDetails, formatInfo?: FormatInfoWrapper) => {
            try {
                setIsSaveFormatLoading(true);
                const queryModelFromGrid = props.liveTableRefs?.queryDetailsRef?.current as | { whereClauses?: SpreadsheetTableWhereClause[] } | undefined;
                let columnsForSave = smartColumns.length > 0 ? smartColumns : tableController.smartColumns || [];
                let whereClausesSource = queryModelFromGrid?.whereClauses || (tableController.queryDetailsRef?.current as any)?.whereClauses || [];

                if (columnsForSave.length === 0 && props.runPayload) {
                    columnsForSave = props.runPayload.columnModel?.smartColumns || [];
                    whereClausesSource = props.runPayload.queryModel?.whereClauses || [];
                }

                if (columnsForSave.length === 0) {
                    throw new Error("Failed to load table data for save format");
                }

                const filteredSmartColumns = columnsForSave.filter(
                    (columnField: SmartColumn) => {
                        const fieldId = columnField.field?.fieldid;
                        const columnOrder = columnField.columnOrder;

                    return fieldId !== SPREADSHEET_TABLE_CONSTANTS.ROW_COUNT_FIELD_ID && columnOrder !== -1;
                    }
                );
                const defaultFieldContext = spreadsheetType === SpreadsheetType.individual ? SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL : SPREADSHEET_FIELD_CONTEXT.PEDIGREE;
                const mappedSmartColumnsForSave = filteredSmartColumns.map(
                    (columnField: SmartColumn, index: number) => {
                        const column: any = {
                            width: columnField.width,
                            sortOrder: columnField.sortOrder,
                            sortAsc: columnField.sortAsc,
                            columnOrder: columnField.columnOrder,
                            field: {
                                fieldid: columnField.field?.fieldid,
                                fieldname: columnField.field?.fieldname,
                                system: columnField.field?.system || false,
                                fieldcontext:
                                    spreadsheetType === SpreadsheetType.individual
                                        ? SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL
                                        : SPREADSHEET_FIELD_CONTEXT.PEDIGREE,
                                type: columnField.field?.type,
                            },
                        };

            // Conditionally add parentField to smartColumns if it exists
            if (columnField.parentField) {
              column.parentField = {
                fieldid: columnField.parentField.fieldid,
                fieldname: columnField.parentField.fieldname,
                system: columnField.parentField.system || false,
                fieldcontext: resolveFieldContextForApi(
                  columnField.parentField,
                  defaultFieldContext
                ),
                type: columnField.parentField.type,
              };
            }

            return column;
          }
        );

                const whereClauses = whereClausesSource.map(
                    (condition: SpreadsheetTableWhereClause, clauseIndex: number) => {
                    const clauseField: any = condition.field;
                    const andOR = (condition as any).andOR ?? (condition as any).andOr;
                    const isLastClause = clauseIndex === whereClausesSource.length - 1;
                    const clause: any = {
                        field: {
                            fieldid: clauseField?.fieldid ?? clauseField?.id,
                            type: clauseField?.system === true ? SPREADSHEET_TABLE_CONSTANTS.FIELD_TYPES.SYSTEM : clauseField?.type,
                            fieldcontext: spreadsheetType === SpreadsheetType.individual ? SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL : SPREADSHEET_FIELD_CONTEXT.PEDIGREE,
                            system: clauseField?.system || false,
                            ...(clauseField?.fieldname || clauseField?.name || clauseField?.fieldName ? { fieldname: clauseField.fieldname || clauseField.name || clauseField.fieldName } : {}),
                        },
                        operator: condition.operator,
                        value: condition.value === undefined || condition.value === null ? "" : String(condition.value),
                        ...(isLastClause ? andOR != null && andOR !== "" ? { andOr: andOR } : {} : { andOr: andOR ?? "AND" }),
                        leftParenthesis: condition.leftParenthesis || false,
                        rightParenthesis: condition.rightParenthesis || false,
                    };
                    if (condition.parentField) {
                        const parentFieldMeta: any = condition.parentField;
                        clause.parentField = {
                            fieldid: parentFieldMeta.fieldid ?? parentFieldMeta.id,
                            type: parentFieldMeta.system === true ? SPREADSHEET_TABLE_CONSTANTS.FIELD_TYPES.SYSTEM : parentFieldMeta.type,
                            fieldcontext: spreadsheetType === SpreadsheetType.individual ? SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL : SPREADSHEET_FIELD_CONTEXT.PEDIGREE,
                            system: parentFieldMeta.system || false,
                            ...(parentFieldMeta.fieldname || parentFieldMeta.fieldName ? { fieldname: parentFieldMeta.fieldname || parentFieldMeta.fieldName } : {}),
                        };
                    }
                    return clause;
                });

                const queryForSave = computeQueryModel(spreadsheetType, {
                    userFilter: { whereClauses },
                    normalizeForSave: true,
                });
                const queryModelForSave = buildLoadIssQueryModelPayload(queryForSave, mappedSmartColumnsForSave, { normalizeIssWhereClauseStrings: true } );
                const cleanedSmartColumns = cleanSmartColumnsForApi(mappedSmartColumnsForSave);

                const payload: SaveFormatPayload = {
                    context:
                        spreadsheetType === SpreadsheetType.individual
                            ? SPREADSHEET_TYPE.individual
                            : SPREADSHEET_TYPE.pedigree,
                    ssFormat: {
                        id: formatInfo?.format?.id || 0,
                        name: formatDetails.name,
                        folderId: formatDetails.folderId,
                        description: formatDetails.description || "",
                    },
                    columnModel: {
                        smartColumns: cleanedSmartColumns,
                    },
                    queryModel: queryModelForSave,
                };

                const result = await saveFormat(payload);
                if (typeof result === "number") {
                    setSpreadsheetId(result);
                }

                const savedFormatId = typeof result === "number" ? result : formatInfo?.format?.id || payload.ssFormat.id || 0;
                if (savedFormatId > 0 && typeof window !== "undefined" && (window as any).__SPREADSHEET_STORE__) {
                    try {
                        const store = (window as any).__SPREADSHEET_STORE__;
                        const { clearFormatData } = require("@modules/spreadsheets/actions/spreadsheetFormat");
                        const spreadsheetTypeKey = spreadsheetType === SpreadsheetType.individual ? "Individual" : "Pedigree";
                        store.dispatch(clearFormatData(savedFormatId, spreadsheetTypeKey));
                    } catch (e) {
                        console.warn("Could not clear format cache", e);
                    }
                }
                formatManagementController.closeSaveFormat();
                return result;
            } catch (err) {
                console.error(err);
                const errorMessage =
                    (err as HttpErrorDto)?.entity || I18n.t("Failed to save formats");
                showErrorMessage(errorMessage);
                throw err;
            } finally {
                setIsSaveFormatLoading(false);
            }
        },
        [
            tableController,
            spreadsheetType,
            showErrorMessage,
            props.runPayload,
            props.liveTableRefs,
            smartColumns,
        ]
    );

    const handleSaveFormat = React.useCallback(
        async (formatDetails: FormatDetails, formatInfo?: FormatInfoWrapper) => {
            try {
                await formatManagementController.getAndSetFormats(
                    formatDetails.folderId
                );

                const existingFormat = formatManagementController.formats.find(
                    (format) =>
                        format.name.trim().toLowerCase() ===
                            formatDetails.name.trim().toLowerCase() &&
                        format.folderId === formatDetails.folderId
                );

                const formatNameBelongsToAnotherSavedFormat = existingFormat && existingFormat.id !== formatInfo?.format?.id;

                if (formatNameBelongsToAnotherSavedFormat) {
                    showConfirmMessage({
                        cancelText: I18n.t("No"),
                        confirmText: I18n.t("Yes"),
                        confirmBtnId: "btn-message-box-ok",
                        cancelBtnId: "btn-message-box-dont-cancel",
                        content: I18n.t(
                            "This name has already been used, would you like to overwrite the current format being used by this name?"
                        ),
                        onCustomOk: async () => {
                            await saveFormatInternal(formatDetails, {
                                format: existingFormat,
                            });
                        },
                    });
                    return;
                }

        await saveFormatInternal(formatDetails, formatInfo);
      } catch (err) {
        console.error(err);
        const errorMessage =
          (err as HttpErrorDto)?.entity || I18n.t("Failed to save formats");
        showErrorMessage(errorMessage);
      }
    },
    [
      formatManagementController,
      showConfirmMessage,
      showErrorMessage,
      saveFormatInternal,
    ]
  );

  const handleAddRow = React.useCallback(async () => {
    if (!selectedRowData || selectedRowData.length === 0) {
      return;
    }

    if (!currentData || currentData.length === 0) {
      return;
    }

    // Prevent rapid clicking by checking if already adding a row
    if (isAddingRow) {
      return;
    }

    const firstSelectedRow = selectedRowData[0];
    if (!firstSelectedRow) return;

    const { globalId, selectedRowRrn, pedigreeRowId } = findGlobalAndRowRrn(
      firstSelectedRow,
      dataResult
    );

    let columnIndex = -1;
    const colIdx = selectedColumnIndex !== null && selectedColumnIndex !== undefined
      ? Number(selectedColumnIndex)
      : -1;

    if (colIdx >= 0 && Array.isArray(smartColumns)) {
      const foundParentField = smartColumns[colIdx]?.parentField;
      if (foundParentField) {
        columnIndex = colIdx;
      } else {
        const firstParentFieldIndex = smartColumns.findIndex(
          (col) => col?.parentField
        );
        columnIndex = firstParentFieldIndex !== -1 ? firstParentFieldIndex : -1;
      }
    }

    // Use cleaned columns to reduce payload size (dropdown/lookup values removed)
    const payloadData = {
      pss: props.spreadsheetType === SpreadsheetType.pedigree,
      rrn: -1,
      column: columnIndex,
      globalId: (props.spreadsheetType === SpreadsheetType.individual ? globalId : pedigreeRowId) || 0,
      columnModel: {
        smartColumns: cleanSmartColumnsForApi(smartColumns || []),
      },
    };

    let loaderShown = false;
    const loaderTimer = setTimeout(() => {
      loaderShown = true;
      setIsAddingRow(true);
    }, 500);
    try {
      const tableId =
        columnIndex ? smartColumns?.[columnIndex]?.parentField?.fieldid : 0;
      const response = await addSSRow(payloadData);

      if (response && response.data && response.rrn) {
        const context =
          props.spreadsheetType === SpreadsheetType.pedigree
            ? SPREADSHEET_TYPE.pedigree
            : SPREADSHEET_TYPE.individual;
        const selectedRowIndex = currentData.findIndex(
          (row) => row.id === firstSelectedRow.id
        );

        // Apply the same logic as cell value changes: use globalId for individual, id for pedigree
        const correctId = props.spreadsheetType === SpreadsheetType.individual ? globalId : pedigreeRowId;

        trackRowAdd(
          selectedRowIndex + 1, // rowIndex where the row was added
          columnIndex as any, // columnId
          correctId || 0, // globalId for individual, pedigreeRowId for pedigree
          tableId, // tableId
          response.rrn // RRN of the newly created row
        );

        if (selectedRowIndex !== -1) {
          const newRow: SpreadsheetTableDefaultDataItem = {
            id: String(selectedRowIndex + 1), // Insert at the correct position
            rowCount: selectedRowIndex + 2, // Adjust rowCount
            rrn: response.rrn,
          };

          if (response.data && response.data.length > 0) {
            response.data.forEach((cellData: CellData) => {
              if (cellData.field && cellData.field.fieldname) {
                // Find the correct cellIndex by looking at the smartColumns array
                // This matches how the original data mapping works
                const smartColumnIndex = smartColumns.findIndex(
                  (col) => {
                    return col.field?.fieldname === cellData.field.fieldname && col.parentField?.fieldid === tableId
                  }
                );

                const parentFieldId = smartColumns[smartColumnIndex].parentField?.fieldid;

                if (smartColumnIndex !== -1 && parentFieldId) {
                  const key = buildRowKey(cellData.field.fieldname, cellData.field.fieldid, parentFieldId);
                  newRow[key] = cellData.dataValue ?? cellData.rawValue ?? "";
                }
              }
            });
          }

          const newData = [...currentData];
          newData.splice(selectedRowIndex + 1, 0, newRow);

          // Update IDs and rowCount for all rows after the insertion
          for (let i = selectedRowIndex + 2; i < newData.length; i++) {
            newData[i].id = String(i);
            newData[i].rowCount = i + 1;
          }
          setCurrentData(newData);
          setTotalCount((prev) => (prev ? prev + 1 : prev));

          // Update localStorage count for saved spreadsheets
          if (props.spreadsheetId && props.spreadsheetId > 0) {
            const currentCount = localStorage.getItem(
              `spreadsheet-count-${props.spreadsheetId}`
            );
            if (currentCount) {
              const newCount = parseInt(currentCount) + 1;
              localStorage.setItem(
                `spreadsheet-count-${props.spreadsheetId}`,
                newCount.toString()
              );
            }
          }

          // Update dataResult state to keep it synchronized
          if (dataResult && dataResult.data && Array.isArray(dataResult.data)) {
            const newDataResult = { ...dataResult };

            // Get the existing row structure to understand the pattern
            const existingRow = newDataResult.data[0];
            if (existingRow && Array.isArray(existingRow)) {
              const rowLength = existingRow.length;

              // Create a complete new row array with the same structure
              const newRowData: (CellData | Record<string, never>)[] =
                new Array(rowLength).fill({});

              // Set the first dummy object (fieldid: -599)
              newRowData[0] = {
                modified: false,
                valid: true,
                rawValue: 0,
                dataValue: 0,
                updateValue: 0,
                constraintViolation: null,
                field: {
                  fieldid: SPREADSHEET_TABLE_CONSTANTS.ROW_COUNT_FIELD_ID,
                  fieldname: "unknown field",
                  permissions: "WRITE",
                  tableIndex: 0,
                  defaultWidth: 100,
                  fieldClass: "java.lang.Integer",
                  system: true,
                  systemDataField: false,
                  required: false,
                  fieldnameWithPath: null,
                  fieldcontext: spreadsheetType === SpreadsheetType.individual
                    ? SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL
                    : SPREADSHEET_FIELD_CONTEXT.PEDIGREE,
                  dataSourceNodeId: 0,
                  fieldtype: {
                    typeid: 3,
                    typename: "Number",
                    imageURL: "nbrhi.bmp",
                    computed: false,
                    systemType: false,
                  },
                  dropdownValues: [],
                },
              };

              // Set the last dummy object (fieldid: -500)
              newRowData[rowLength - 1] = {
                modified: false,
                valid: true,
                rawValue: "",
                dataValue: "",
                updateValue: "",
                constraintViolation: null,
                field: {
                  fieldid: -500,
                  fieldname: "",
                  permissions: "WRITE",
                  tableIndex: 0,
                  defaultWidth: 100,
                  fieldClass: "java.lang.String",
                  system: true,
                  systemDataField: false,
                  required: false,
                  fieldnameWithPath: null,
                  fieldcontext: spreadsheetType === SpreadsheetType.individual
                    ? SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL
                    : SPREADSHEET_FIELD_CONTEXT.PEDIGREE,
                  dataSourceNodeId: 0,
                  fieldtype: {
                    typeid: 3,
                    typename: "Number",
                    imageURL: "nbrhi.bmp",
                    computed: false,
                    systemType: false,
                  },
                  dropdownValues: [],
                },
              };

              // Fill the middle positions with empty objects or response data
              const responseDataForDataResult =
                response.data?.data || response.data || [];
              let responseDataIndex = 0;
              for (let i = 1; i < rowLength - 1; i++) {
                const currentSmartColumn = smartColumns[i];
                const { parentField } = currentSmartColumn;
                if (parentField && parentField.fieldid === tableId) {
                  // This is the column where add row was clicked, insert the response data
                  if (
                    responseDataForDataResult &&
                    responseDataForDataResult.length > 0
                  ) {
                    // Use the first response data object and add RRN
                    const responseCell =
                      responseDataForDataResult[responseDataIndex];
                    if (
                      currentSmartColumn?.field?.fieldname ===
                      responseCell?.field?.fieldname && currentSmartColumn?.parentField?.fieldid === tableId
                    ) {
                      newRowData[i] = {
                        ...responseCell,
                        rrn: response.rrn,
                        tableId: tableId
                      };
                      responseDataIndex++;
                    }
                  }
                }
              }

              // Insert the new row data into dataResult.data
              newDataResult.data.splice(selectedRowIndex + 1, 0, newRowData);

              if (newDataResult.rows && Array.isArray(newDataResult.rows)) {
                // Apply the same logic as cell value changes: use globalId for individual, id for pedigree
                const correctId = props.spreadsheetType === SpreadsheetType.individual ? globalId : pedigreeRowId;

                const newRowMeta = {
                  globalid: globalId,
                  id: pedigreeRowId,
                  rrn: response.rrn,
                };
                newDataResult.rows.splice(selectedRowIndex + 1, 0, newRowMeta);
              }

              setDataResult(newDataResult);
            }
          }
        }
      }
    } catch (error) {
      console.error("Failed to add row:", error);
      const errorMessage =
        (error as HttpErrorDto)?.entity || I18n.t("Failed to add row");
      showErrorMessage(errorMessage);
    } finally {
      clearTimeout(loaderTimer);
      if (loaderShown) {
        setIsAddingRow(false);
      }
    }
  }, [
    selectedRowData,
    currentData,
    smartColumns,
    dataResult,
    props.spreadsheetType,
    showErrorMessage,
    selectedColumnIndex,
    isAddingRow,
  ]);

  const handleDeleteRow = React.useCallback(async () => {
    if (!selectedRowData || selectedRowData.length === 0) {
      return;
    }
    if (!currentData || currentData.length === 0) {
      return;
    }

    const rowsToDelete = selectedRowData.filter((row) => row?.rrn);
    if (rowsToDelete.length === 0) {
      return;
    }

    let loaderShown = false;
    const loaderTimer = setTimeout(() => {
      loaderShown = true;
      setIsDeletingRow(true);
    }, 500);

    try {
      // Collect all deleted row data for bulk undo tracking
      const allDeletedRowsData: Array<{
        rowIndex: number;
        columnId: SpreadsheetTableColumnId;
        globalId: number;
        tableId: number;
        rrn: number;
        deletedRowData: Array<DeletedRowData>;
      }> = [];

      const deletePromises = rowsToDelete.map(async (row) => {
        const { globalId, selectedRowRrn, pedigreeRowId } = findGlobalAndRowRrn(
          row,
          dataResult
        );

        let columnIndex = -1;
        if (dataResult?.data) {
          const clickedRow = dataResult.data[Number(row.id)];
          if (clickedRow && selectedRowRrn) {
            columnIndex = clickedRow.findIndex(
              (cell: any) => cell?.rrn === selectedRowRrn
            );
          }
        }

        // Store the deleted row data for undo functionality
        const deletedRowData: Array<DeletedRowData> = [];

        const currentRowData = currentData[Number(row.id)];
        if (
          currentRowData &&
          dataResult?.data &&
          dataResult.data[Number(row.id)]
        ) {
          const backendRowData = dataResult.data[Number(row.id)];
          backendRowData.forEach((cell: any) => {
            if (cell?.field?.fieldid && cell?.field?.fieldid > 0 && cell?.rrn) {
              const fieldName = cell.field.fieldname;
              const tableId = cell.tableId
              // Find the correct cellIndex by looking at the smartColumns array
              const smartColumnIndex = smartColumns.findIndex(
                (col) => {
                  return col.field?.fieldname === fieldName && col.parentField?.fieldid === tableId
                }
              );

              if (smartColumnIndex !== -1) {
                const key = buildRowKey(fieldName, cell.field.fieldid, tableId);
                const currentValue = currentRowData[key];
                const backendValue = cell.rawValue || cell.dataValue || "";

                deletedRowData.push({
                  fieldId: cell.field.fieldid,
                  value:
                    currentValue !== undefined ? currentValue : backendValue,
                  rrn: cell.rrn,
                  tableId: tableId || 0,
                  columnIndex: smartColumnIndex,
                });
              }
            }
          });
        }

        // Use cleaned columns to reduce payload size (dropdown/lookup values removed)
        const payloadData = {
          pss: props.spreadsheetType === SpreadsheetType.pedigree,
          rrn: selectedRowRrn || 0,
          column: columnIndex || -1,
          columnModel: {
            smartColumns: cleanSmartColumnsForApi(smartColumns || []),
          },
        };

        const response = await deleteSSRow(payloadData);

        if (response && response.code === 200) {

          const tableId = smartColumns?.[columnIndex]?.parentField?.fieldid || 0;

          // Store the deleted row data for bulk undo tracking
          const correctId = props.spreadsheetType === SpreadsheetType.individual ? globalId : pedigreeRowId;


          allDeletedRowsData.push({
            rowIndex: Number(row.id), // rowIndex where the row was deleted
            columnId: columnIndex,
            globalId: correctId || 0,
            tableId: tableId,
            rrn: selectedRowRrn || 0, // RRN of the deleted row
            deletedRowData: deletedRowData,
          });
        }
        return response;
      });

      const responses = await Promise.all(deletePromises);

      // Check if all deletions were successful
      const successfulDeletions = responses.filter(
        (response: DeleteSsRowResponse | null) =>
          response && response.code === 200
      );

      if (successfulDeletions.length === rowsToDelete.length) {
        // Track all deleted rows as a single bulk operation
        if (trackRowDelete && allDeletedRowsData.length > 0) {
          trackRowDelete(allDeletedRowsData);
        }

        const newData = [...currentData];
        const deletedIds = rowsToDelete.map((row) => row.id);

        for (let i = newData.length - 1; i >= 0; i--) {
          if (deletedIds.includes(newData[i].id)) {
            newData.splice(i, 1);
          }
        }

        for (let i = 0; i < newData.length; i++) {
          newData[i].id = String(i);
          newData[i].rowCount = i + 1;
        }

        setCurrentData(newData);
        setTotalCount((prev) => (prev ? prev - rowsToDelete.length : prev));

        // Update localStorage count for saved spreadsheets
        if (props.spreadsheetId && props.spreadsheetId > 0) {
          const currentCount = localStorage.getItem(
            `spreadsheet-count-${props.spreadsheetId}`
          );
          if (currentCount) {
            const newCount = Math.max(
              0,
              parseInt(currentCount) - rowsToDelete.length
            );
            localStorage.setItem(
              `spreadsheet-count-${props.spreadsheetId}`,
              newCount.toString()
            );
          }
        }
        setSelectedRowData([]);

        if (dataResult && dataResult.data && Array.isArray(dataResult.data)) {
          const newDataResult = { ...dataResult };

          for (let i = newDataResult.data.length - 1; i >= 0; i--) {
            const rowData = newDataResult.data[i];

            const shouldDelete = rowsToDelete.some((row) => {
              return rowData.some((cell: CellData) => cell?.rrn === row.rrn);
            });

            if (shouldDelete) {
              newDataResult.data.splice(i, 1);
              if (newDataResult.rows && Array.isArray(newDataResult.rows)) {
                newDataResult.rows.splice(i, 1);
              }
            }
          }

          setDataResult(newDataResult);
        }
      }
    } catch (error) {
      console.error("Failed to delete rows:", error);
      const errorMessage =
        (error as HttpErrorDto)?.entity || I18n.t("Failed to delete rows");
      showErrorMessage(errorMessage);
    } finally {
      clearTimeout(loaderTimer);
      setClearSelectionEvent(true)
      if (loaderShown) {
        setIsDeletingRow(false);
      }
    }
  }, [
    selectedRowData,
    currentData,
    smartColumns,
    dataResult,
    props.spreadsheetType,
    showErrorMessage,
    trackRowDelete,
    selectedColumnIndex,
  ]);

  const spreadsheetTypeConfig: Record<
    SpreadsheetType,
    { loadFmtTitle: string; saveFmtTitle: string }
  > = {
    [SpreadsheetType.pedigree]: {
      loadFmtTitle: I18n.t("Load Pedigree Spreadsheet Format"),
      saveFmtTitle: I18n.t("Save Pedigree Spreadsheet Format"),
    },
    [SpreadsheetType.individual]: {
      loadFmtTitle: I18n.t("Load Individual Spreadsheet Format"),
      saveFmtTitle: I18n.t("Save Individual Spreadsheet Format"),
    },
  };
  const currentSpreadsheetTypeConfig = spreadsheetTypeConfig[spreadsheetType];

  const sendBatchNewInvites = async (inviteData: {
    questionnaireId: number;
    fhq4: boolean;
    emailTemplate: {
      subject: string;
      body: string;
    };
  }) => {
    await sendBatchInvitesController.sendBatchInvites(inviteData);
  };

  const handleBatchLetters = React.useCallback(() => {
    if (!dataResult || !dataResult.rows || dataResult.rows.length === 0) {
      showErrorMessage(I18n.t("No data available for batch letters"));
      return;
    }

    const globalIds: number[] = [];
    dataResult.rows.forEach((row: any) => {
      if (row.globalid) {
        globalIds.push(row.globalid);
      }
    });

    if (globalIds.length === 0) {
      showErrorMessage(I18n.t("No valid global IDs found in spreadsheet data"));
      return;
    }

    // Call the batch letters function
    createBatchLetters(globalIds.map(id => id.toString()));
  }, [dataResult, showErrorMessage]);

  // ============================================================================
  // COUNT API
  // ============================================================================
  const getCount = React.useCallback(async (): Promise<number> => {
    const reduxFormatData = props.spreadsheetId
      ? getFormatDataFromRedux(props.spreadsheetId, props.spreadsheetType)
      : null;

    const columns = (smartColumns && smartColumns.length > 0) ? smartColumns :
        (props.runPayload?.columnModel?.smartColumns?.length ? props.runPayload.columnModel.smartColumns : (reduxFormatData?.columnModel?.smartColumns || []));

    if (!columns || columns.length === 0) {
      throw new Error(I18n.t('No columns available to compute count'));
    }

    // Build query and filter separately (do not merge)
    const runPayloadFilter = props.runPayload && (props.runPayload as any).filter
      ? JSON.parse(JSON.stringify((props.runPayload as any).filter))
      : undefined;
    const userFilter = tableController.activeFilterRef?.current ?? runPayloadFilter ?? reduxFormatData?.filter;

    const queryFromRefs = props.liveTableRefs?.queryDetailsRef?.current ?? tableController.queryDetailsRef?.current;

    let rawQueryModel: { whereClauses?: any[] };
    if (props.runPayload && props.runPayload.queryModel !== undefined) {
      rawQueryModel = JSON.parse(JSON.stringify(props.runPayload.queryModel));
    } else if (queryFromRefs && Array.isArray(queryFromRefs.whereClauses)) {
      rawQueryModel = JSON.parse(JSON.stringify(queryFromRefs));
    } else if (reduxFormatData?.queryModel && reduxFormatData.queryModel.whereClauses) {
      rawQueryModel = reduxFormatData.queryModel;
    } else {
      rawQueryModel = { whereClauses: [] };
    }

    // Minimize whereClauses: include minimal field and optional parentField, normalize value
    const cleanQueryModel = (qm: any) => {
      const normalizeValue = (val: any): string | number => {
        if (val == null) return "";
        if (typeof val === "string") return val;
        if (typeof val === "number") return val;
        if (typeof val === "object") {
          return val.dataValue;

        }
        return "";
      };

      const minimalField = (fld: any) => {
        if (!fld) return undefined;
        const typeFromField = fld?.fieldtype?.typeid ?? fld?.type;
        return {
          fieldid: fld.fieldid,
          type: typeFromField,
          fieldcontext: fld.fieldcontext,
          system: Boolean(fld.system),
        };
      };

      return {
        whereClauses: (qm?.whereClauses || []).map((cl: any) => {
          const fld = cl?.field || cl?.value?.field;
          return {
            field: minimalField(fld),
            parentField: minimalField(cl?.parentField),
            operator: cl?.operator,
            value: normalizeValue(cl?.value),
            fieldClass: cl?.fieldClass || fld?.fieldClass,
            leftParenthesis: Boolean(cl?.leftParenthesis ?? cl?.leftParanthesis),
            rightParenthesis: Boolean(cl?.rightParenthesis ?? cl?.rightParanthesis),
            andOr: cl?.andOr || cl?.andOR,
          };
        }),
      };
    };

    const normalizedQueryModel = cleanQueryModel(rawQueryModel);

    const payload: any = {
      folderId: props.isTemporaryRun
        ? (props.runPayload?.folderId ?? props.runPayload?.ssFormat?.folderId ?? props.initialFolderId ?? 0)
        : (props.initialFolderId ?? 0),
      familyId: props.spreadsheetType === SpreadsheetType.individual
        ? (props.runPayload?.familyId ?? props.initialFamilyId ?? 0)
        : 0,
      context: props.spreadsheetType === SpreadsheetType.individual
        ? SPREADSHEET_TYPE.individual
        : SPREADSHEET_TYPE.pedigree,
      columnModel: { smartColumns: cleanSmartColumnsForApi(columns) },
      queryModel: cleanQueryModelForApi(normalizedQueryModel),
      filter: userFilter ? cleanQueryModel(userFilter) : { whereClauses: [] },
    };

    let count: any = await fetchSpreadsheetCount(payload);
    count = Number(count);
    return isFinite(count) ? count : 0;
  }, [smartColumns, props.runPayload, props.isTemporaryRun, props.liveTableRefs, tableController.queryDetailsRef, tableController.activeFilterRef, props.spreadsheetId, props.spreadsheetType, props.initialFolderId, props.initialFamilyId]);

  return {
    sendBatchInvitesController,
    sendBatchNewInvites,
    handleSaveFormat,
    currentSpreadsheetTypeConfig,
    handleLoadFormat,
    messageProps,
    closeMessage,
    showInfoMessage,
    showMessage,
    showErrorMessage,
    dataImportController,
    formatManagementController,
    dataExportController,
    handleAddRow,
    handleDeleteRow,
    handleBatchLetters,
    isAddingRow,
    isDeletingRow,
    setSelectedRowData,
    setCurrentData,
    setSmartColumns,
    setDataResult,
    setTotalCount,
    setHasRrn,
    setHasParentField,
    setSelectedColumnIndex,
    setCanAddRow,
    setCanDeleteRow,
    hasRrn,
    hasParentField,
    canAddRow,
    canDeleteRow,
    currentData,
    smartColumns,
    isSaveFormatLoading,
    spreadsheetId,
    setSpreadsheetId,
    totalCount,
    undoRedoState,
    trackCellEdit,
    trackRowAdd,
    trackRowDelete,
    trackBulkFill,
    undoRedoCallbacks,
    getCount,
    setClearSelectionEvent,
    clearSelectionEvent, handleEdit,
    tableController,
    isProcessingUndoRedo
  };
};
